import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const AuthContext = createContext(null);

const ACCESS_KEY = "opsora_access_token";
const REFRESH_KEY = "opsora_refresh_token";
const USER_KEY = "opsora_user";

function readUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readUser);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem(ACCESS_KEY) || null);
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem(REFRESH_KEY) || null);

  const login = useCallback((tokenData) => {
    localStorage.setItem(ACCESS_KEY, tokenData.access_token);
    localStorage.setItem(REFRESH_KEY, tokenData.refresh_token);
    localStorage.setItem(USER_KEY, JSON.stringify(tokenData.user));
    setAccessToken(tokenData.access_token);
    setRefreshToken(tokenData.refresh_token);
    setUser(tokenData.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  // Keep tokens in sync across tabs
  useEffect(() => {
    const sync = (e) => {
      if (e.key === ACCESS_KEY) setAccessToken(e.newValue);
      if (e.key === REFRESH_KEY) setRefreshToken(e.newValue);
      if (e.key === USER_KEY) setUser(readUser());
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  return (
    <AuthContext.Provider value={{ user, accessToken, refreshToken, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

// Pure helpers for non-component code (axios interceptor)
export const authStorage = {
  getAccess: () => localStorage.getItem(ACCESS_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  setTokens: (tokenData) => {
    localStorage.setItem(ACCESS_KEY, tokenData.access_token);
    localStorage.setItem(REFRESH_KEY, tokenData.refresh_token);
    localStorage.setItem(USER_KEY, JSON.stringify(tokenData.user));
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};
